// Error Boundary Component for better error handling
// Place this in your React project's src/components/ErrorBoundary.jsx

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console or error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2>Oops! Something went wrong</h2>
            <p>
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre>{this.state.error && this.state.error.toString()}</pre>
                <pre>{this.state.errorInfo?.componentStack || 'No component stack available'}</pre>
              </details>
            )}
            
            <div className="error-actions">
              <button 
                className="btn-retry"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              
              <button 
                className="btn-reset"
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple error fallback component (no external dependencies needed)
export const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="error-boundary">
      <div className="error-boundary-content">
        <h2>Oops! Something went wrong</h2>
        <p>
          We're sorry, but something unexpected happened. Please try again.
        </p>
        
        {process.env.NODE_ENV === 'development' && (
          <details className="error-details">
            <summary>Error Details (Development)</summary>
            <pre>{error?.message || 'Unknown error'}</pre>
            <pre>{error?.stack || 'No stack trace available'}</pre>
          </details>
        )}
        
        <div className="error-actions">
          <button 
            className="btn-retry"
            onClick={resetErrorBoundary}
          >
            Try Again
          </button>
          
          <button 
            className="btn-refresh"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;